import { StyleProp, View, ViewStyle } from 'react-native';
import React from 'react';
import SkeletonLoadingProvider from '../SkeletonLoadingProvider';
import IsDisabledProvider from '../IsDisabledProvider';
import Skeleton from '../Skeleton';

export type ContainerDimensions = { width: number; height: number };

export type RenderChildrenWithExistingContainerDimensions = (
    containerDimensions: ContainerDimensions,
) => React.ReactNode;

export type SkeletonLoadingContainerOwnProps = {
    isLoading: boolean;
    children: React.ReactNode | RenderChildrenWithExistingContainerDimensions;
    overlayOpacity?: number;
    skeletonStyle?: StyleProp<ViewStyle>;
    loadingSkeletonStyle?: StyleProp<ViewStyle>;
};

export type SkeletonLoadingContainerProps = SkeletonLoadingContainerOwnProps;

const IsInSkeletonLoadingContainerContext = React.createContext(false);

export const useIsInLoadingContainer = () => React.useContext(IsInSkeletonLoadingContainerContext);

const SkeletonLoadingContainer: React.FC<SkeletonLoadingContainerProps> = (props) => {
    const { isLoading, children } = props;
    const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });

    const renderChildren = () => (typeof children === 'function' ? children(containerSize) : children);

    if (!isLoading) return <>{renderChildren()}</>;

    return (
        <IsDisabledProvider isDisabled>
            <SkeletonLoadingProvider isSkeletonLoading={true}>
                <IsInSkeletonLoadingContainerContext.Provider value={true}>
                    <View
                        onLayout={({
                            nativeEvent: {
                                layout: { width, height },
                            },
                        }) => setContainerSize({ width, height })}
                        style={[props.skeletonStyle, isLoading ? props.loadingSkeletonStyle : {}]}
                    >
                        {isLoading ? (
                            <Skeleton
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    opacity: props.overlayOpacity || 1,
                                    width: containerSize.width,
                                    height: containerSize.height,
                                }}
                                testID="skeleton-loading-container"
                            />
                        ) : null}
                        <View style={{ opacity: isLoading && !props.overlayOpacity ? 0 : 1 }}>{renderChildren()}</View>
                    </View>
                </IsInSkeletonLoadingContainerContext.Provider>
            </SkeletonLoadingProvider>
        </IsDisabledProvider>
    );
};

export default React.memo(SkeletonLoadingContainer);
